import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

export const MIN_SIDEBAR_WIDTH = 260;
export const DEFAULT_SIDEBAR_WIDTH = 300;

const SIDEBAR_WIDTH_KEY = 'homechat.sidebarWidth';

// Порция записи ширины: перетаскивание не должно писать в localStorage каждый кадр.
const PERSIST_DELAY_MS = 150;

// Тестируется отдельно и нужна для повторного ограничения ширины из App.
// eslint-disable-next-line react-refresh/only-export-components
export function clampSidebarWidth(value: number, windowWidth: number): number {
  const maximum = Number.isFinite(windowWidth)
    ? Math.floor(windowWidth / 3)
    : MIN_SIDEBAR_WIDTH;
  if (maximum < MIN_SIDEBAR_WIDTH) return MIN_SIDEBAR_WIDTH;
  if (!Number.isFinite(value)) return MIN_SIDEBAR_WIDTH;
  return Math.min(maximum, Math.max(MIN_SIDEBAR_WIDTH, value));
}

function readSidebarWidth(): number {
  try {
    const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return stored === null ? DEFAULT_SIDEBAR_WIDTH : Number(stored);
  } catch {
    return DEFAULT_SIDEBAR_WIDTH;
  }
}

// Хук хранит ширину для корневого layout, а не локально в разделителе.
// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarWidth(): [number, (value: number) => void] {
  const [width, setWidthState] = useState(() => clampSidebarWidth(readSidebarWidth(), window.innerWidth));
  const widthRef = useRef(width);
  const persistTimerRef = useRef<number | null>(null);

  // Запись в localStorage — синхронная, поэтому при перетаскивании она откладывается:
  // иначе каждый pointermove блокирует кадр. Сохраняем последнее значение порциями.
  const persist = useCallback((nextWidth: number) => {
    if (persistTimerRef.current !== null) window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(() => {
      persistTimerRef.current = null;
      try {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(nextWidth));
      } catch {
        // Если хранилище недоступно, ширина всё равно действует до закрытия страницы.
      }
    }, PERSIST_DELAY_MS);
  }, []);

  const setWidth = useCallback((value: number) => {
    const nextWidth = clampSidebarWidth(value, window.innerWidth);
    widthRef.current = nextWidth;
    setWidthState(nextWidth);
    persist(nextWidth);
  }, [persist]);

  useEffect(() => () => {
    if (persistTimerRef.current === null) return;
    window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = null;
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(widthRef.current));
    } catch {
      // При закрытии страницы сохранить не удалось — останется прошлое значение.
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setWidth(widthRef.current);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setWidth]);

  return [width, setWidth];
}

interface SidebarResizerProps {
  width: number;
  onWidthChange: (width: number) => void;
}

export function SidebarResizer({ width, onWidthChange }: SidebarResizerProps) {
  const dragRef = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);
  const previousUserSelectRef = useRef<string | null>(null);

  const finishDrag = useCallback((event?: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current && event && dragRef.current.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (previousUserSelectRef.current !== null) {
      document.body.style.userSelect = previousUserSelectRef.current;
      previousUserSelectRef.current = null;
    }
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  useEffect(() => () => {
    if (previousUserSelectRef.current !== null) {
      document.body.style.userSelect = previousUserSelectRef.current;
      previousUserSelectRef.current = null;
    }
  }, []);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || dragRef.current) return;
    event.preventDefault();
    dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: width };
    previousUserSelectRef.current = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    onWidthChange(clampSidebarWidth(drag.startWidth + event.clientX - drag.startX, window.innerWidth));
  };

  return (
    <div
      className="sidebar-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-valuemin={MIN_SIDEBAR_WIDTH}
      aria-valuemax={Math.max(MIN_SIDEBAR_WIDTH, Math.floor(window.innerWidth / 3))}
      aria-valuenow={width}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onLostPointerCapture={() => finishDrag()}
      onDoubleClick={() => onWidthChange(DEFAULT_SIDEBAR_WIDTH)}
    />
  );
}

export default SidebarResizer;
